import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { StickyNote, Mail, ArrowLeft } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { ButtonSpinner } from '../../components/common/LoadingSpinner'
import { apiUtils } from '../../services/api'
import noteSparkLogo from '../../assets/notespark.png'

/**
 * Forgot Password Page Component
 * 
 * Allows users to request a password reset by providing their email address.
 * Sends a new password to the user's email.
 */
const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth()
  const { currentTheme, setTheme } = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  // Force light mode for forgot password page - captive portal should never use dark mode
  useEffect(() => {
    const previousTheme = currentTheme
    if (currentTheme !== 'light') {
      setTheme('light')
    }
    return () => {
      // Restore previous theme when leaving the page
      if (previousTheme !== 'light') {
        setTheme(previousTheme)
      }
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    getValues
  } = useForm({
    defaultValues: {
      email: ''
    }
  })

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      clearErrors()

      const result = await forgotPassword(data.email)
      
      if (result.success) {
        setIsSubmitted(true)
      } else {
        setError('email', { message: result.error })
      }
    } catch (error) {
      setError('root', { 
        message: 'An unexpected error occurred. Please try again.' 
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen auth-gradient-bg flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <StickyNote className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-normal text-gray-900">
            Check your email
          </h2>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-gray-600 mb-6">
                We've sent a new password to <strong>{getValues('email')}</strong>. 
                Please check your inbox and use the new password to log in.
              </p>
              <div className="space-y-4">
                <Link
                  to="/login"
                  className="btn-primary w-full"
                >
                  Back to Login
                </Link>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="btn-outline w-full"
                >
                  Try Another Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen auth-gradient-bg flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Title */}
        <div className="flex justify-center items-center space-x-2 sm:space-x-3">
          <img src={noteSparkLogo} alt="NoteSpark Logo" className="h-10 w-10 sm:h-12 sm:w-12" />
          <span className="text-xl sm:text-2xl font-bold text-gray-900">notespark</span>
        </div>
        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-normal text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your email address and we'll send you a new password.
        </p>
      </div>

      <div className="mt-4 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Global Error */}
            {errors.root && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">
                  {errors.root.message}
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  className={`appearance-none block w-full pl-12 pr-3 py-2 border rounded-md placeholder-gray-400 text-blue-600 focus:outline-none sm:text-sm ${errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                  placeholder="Enter your email address"
                />
              </div>
              {errors.email && (
                <p className="form-error">{errors.email.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full"
              >
                {isLoading ? (
                  <>
                    <ButtonSpinner className="mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send New Password'
                )}
              </button>
            </div>
          </form>

          {/* Back to Login */}
          <div className="mt-6">
            <Link
              to="/login"
              className="flex items-center justify-center text-sm text-indigo-600 hover:text-indigo-500"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage 